import React, { useState, useEffect, useCallback } from 'react';
import { housesAPI, itemsAPI, groceriesAPI, projectsAPI } from '../services/api';
import MedicationAlerts from './MedicationAlerts';
import BudgetSummary from './BudgetSummary';
import './Dashboard.css';

const Dashboard = ({ selectedHouseId, onHouseSelect, onNavigate }) => {
  const [stats, setStats] = useState({
    lowStockItems: 0,
    expiringItems: 0,
    groceryItems: 0,
    purchasedGroceries: 0,
  });
  const [groceryList, setGroceryList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [houses, setHouses] = useState([]);
  const [loadingHouses, setLoadingHouses] = useState(false);

  // Load houses list for selection UI (only when no house is selected)
  useEffect(() => {
    if (!selectedHouseId) {
      const loadHousesList = async () => {
        setLoadingHouses(true);
        try {
          const response = await housesAPI.getAll();
          const housesData = Array.isArray(response.data) ? response.data : [];
          // Sort houses by ID to ensure consistent ordering
          housesData.sort((a, b) => (a.id || 0) - (b.id || 0));
          setHouses(housesData);
          
          // Don't auto-restore saved house - let user select from Dashboard
          // This ensures first-time selection happens in Dashboard UI
        } catch (error) {
          console.error('Error loading houses:', error);
        } finally {
          setLoadingHouses(false);
        }
      };
      loadHousesList();
    } else {
      // Clear houses list when a house is selected to avoid confusion
      setHouses([]);
    }
  }, [selectedHouseId]);

  const loadDashboardData = useCallback(async () => {
    if (!selectedHouseId) {
      setLoading(false);
      setSelectedHouse(null);
      return;
    }

    try {
      setLoading(true);
      // Fetch the selected house
      let house = null;
      try {
        const houseResponse = await housesAPI.getById(selectedHouseId);
        // Handle both direct data and response.data structures
        house = houseResponse?.data || houseResponse;
        if (!house || !house.id) {
          throw new Error('Invalid house response structure');
        }
      } catch (error) {
        console.warn(`getById failed for house ${selectedHouseId}, trying getAll fallback:`, error);
        // Fallback to getAll if getById fails
        try {
          const housesResponse = await housesAPI.getAll();
          const housesData = Array.isArray(housesResponse.data) ? housesResponse.data : [];
          // Sort houses by ID to ensure consistent ordering
          housesData.sort((a, b) => (a.id || 0) - (b.id || 0));
          house = housesData.find(h => h.id === selectedHouseId);
          if (!house) {
            console.error(`House ${selectedHouseId} not found in houses list`);
          }
        } catch (fallbackError) {
          console.error('Fallback getAll also failed:', fallbackError);
        }
      }

      if (!house) {
        console.error(`House with ID ${selectedHouseId} not found`);
        setSelectedHouse(null);
        setLoading(false);
        localStorage.removeItem('selectedHouseId');
        if (onHouseSelect) onHouseSelect(null);
        return;
      }

      setSelectedHouse(house);

      // Load dashboard data in parallel
      const [
        lowStockResponse,
        expiringResponse,
        groceriesResponse,
        projectsResponse
      ] = await Promise.all([
        itemsAPI.getLowStock(house.id).catch((err) => {
          console.warn('Error loading low stock items:', err);
          return { data: [] };
        }),
        itemsAPI.getExpiring(house.id, 30).catch((err) => {
          console.warn('Error loading expiring items:', err);
          return { data: [] };
        }),
        groceriesAPI.getAll(house.id).catch((err) => {
          console.warn('Error loading groceries:', err);
          return { data: [] };
        }),
        projectsAPI.getAll(house.id).catch((err) => {
          console.warn('Error loading projects:', err);
          return { data: [] };
        })
      ]);

      const lowStock = Array.isArray(lowStockResponse.data) ? lowStockResponse.data : [];
      const expiring = Array.isArray(expiringResponse.data) ? expiringResponse.data : [];
      const groceries = Array.isArray(groceriesResponse.data) ? groceriesResponse.data : [];
      const projectsData = Array.isArray(projectsResponse.data) ? projectsResponse.data : [];
      const purchasedGroceries = groceries.filter(g => g.is_purchased).length;

      setStats({
        lowStockItems: lowStock.length,
        expiringItems: expiring.length,
        groceryItems: groceries.length,
        purchasedGroceries,
      });

      setGroceryList(groceries.filter(g => !g.is_purchased).slice(0, 5));
      
      // Sort projects by priority and status, show active ones first
      const sortedProjects = projectsData
        .filter(p => {
          const status = typeof p.status === 'string' ? p.status : (p.status?.value || 'planned');
          return status !== 'completed' && status !== 'cancelled';
        })
        .sort((a, b) => {
          // Sort by status: in_progress > on_hold > planned
          const statusOrder = { 'in_progress': 0, 'on_hold': 1, 'planned': 2 };
          const statusA = typeof a.status === 'string' ? a.status : (a.status?.value || 'planned');
          const statusB = typeof b.status === 'string' ? b.status : (b.status?.value || 'planned');
          if (statusOrder[statusA] !== statusOrder[statusB]) {
            return statusOrder[statusA] - statusOrder[statusB];
          }
          // Then by priority: urgent > high > medium > low
          const priorityOrder = { 'urgent': 0, 'high': 1, 'medium': 2, 'low': 3 };
          const priorityA = typeof a.priority === 'string' ? a.priority : (a.priority?.value || 'medium');
          const priorityB = typeof b.priority === 'string' ? b.priority : (b.priority?.value || 'medium');
          return priorityOrder[priorityA] - priorityOrder[priorityB];
        })
        .slice(0, 5); // Show top 5 projects
      
      setProjects(sortedProjects);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
      // Don't clear on generic errors (e.g. network) - only clear when house explicitly not found
    } finally {
      setLoading(false);
    }
  }, [selectedHouseId, onHouseSelect]);

  // Call loadDashboardData when selectedHouseId changes
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show house selection UI if no house is selected
  if (!selectedHouseId) {
    return (
      <div className="dashboard">
        <div className="dashboard-empty">
          <div className="empty-icon">🏠</div>
          <h2>Welcome to HomeManager!</h2>
          <p>Please select a house to get started</p>
          {loadingHouses ? (
            <div className="loading-spinner" style={{ marginTop: '20px' }}></div>
          ) : houses.length > 0 ? (
            <div className="house-selection-list" style={{ marginTop: '30px', maxWidth: '400px', margin: '30px auto 0' }}>
              {houses.map((house) => (
                <button
                  key={house.id}
                  className="house-selection-button"
                  onClick={() => {
                    localStorage.setItem('selectedHouseId', house.id.toString());
                    if (onHouseSelect) {
                      onHouseSelect(house.id);
                    }
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '15px 20px',
                    marginBottom: '10px',
                    fontSize: '16px',
                    textAlign: 'left',
                    backgroundColor: 'var(--card-bg, #fff)',
                    border: '2px solid var(--border-color, #e0e0e0)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary-light, #f0f0f0)';
                    e.currentTarget.style.borderColor = 'var(--primary, #007bff)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--card-bg, #fff)';
                    e.currentTarget.style.borderColor = 'var(--border-color, #e0e0e0)';
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{house.name}</div>
                  {house.address && (
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary, #666)' }}>{house.address}</div>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <p style={{ marginTop: '20px', color: 'var(--text-secondary, #666)' }}>
              No houses available. Please create a house first.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Show loading state only when actively loading (not when house is missing due to error)
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading house data...</p>
      </div>
    );
  }

  // This should not happen if logic is correct, but handle it gracefully
  if (selectedHouseId && !selectedHouse) {
    return (
      <div className="dashboard">
        <div className="dashboard-empty">
          <div className="empty-icon">🏠</div>
          <h2>House not found</h2>
          <p>House ID {selectedHouseId} could not be loaded. Please select a different house.</p>
          <button
            onClick={() => {
              localStorage.removeItem('selectedHouseId');
              if (onHouseSelect) {
                onHouseSelect(null);
              }
            }}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Select House
          </button>
        </div>
      </div>
    );
  }

  const groceryProgress = stats.groceryItems > 0 
    ? Math.round((stats.purchasedGroceries / stats.groceryItems) * 100)
    : 0;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>{selectedHouse.name}</h1>
        </div>
      </div>
      
      {/* Only show alert stats if there are any */}
      {(stats.lowStockItems > 0 || stats.expiringItems > 0) && (
        <div className="stats-grid">
          {stats.lowStockItems > 0 && (
            <div className="stat-card alert">
              <div className="stat-icon-wrapper">
                <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.lowStockItems}</div>
                <div className="stat-label">Low Stock Items</div>
              </div>
            </div>
          )}

          {stats.expiringItems > 0 && (
            <div className="stat-card warning">
              <div className="stat-icon-wrapper">
                <svg className="stat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.expiringItems}</div>
                <div className="stat-label">Expiring Soon</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main content grid */}
      <div className="dashboard-main">
        <div className="dashboard-left">
          {stats.groceryItems > 0 && (
            <div 
              className="dashboard-card clickable-card" 
              onClick={() => onNavigate && onNavigate('inventory', 'groceries')} 
              style={{ cursor: 'pointer' }}
            >
              <div className="card-header">
                <h2>
                  <svg className="card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Grocery List
                </h2>
                <div className="card-header-info">
                  <span className="card-badge">{stats.groceryItems} items</span>
                  {groceryProgress > 0 && (
                    <span className="card-progress">{groceryProgress}% done</span>
                  )}
                </div>
              </div>
              {groceryProgress > 0 && (
                <div className="progress-bar" style={{ marginBottom: 'var(--spacing-md)' }}>
                  <div 
                    className="progress-fill" 
                    style={{ width: `${groceryProgress}%` }}
                  ></div>
                </div>
              )}
              {groceryList.length > 0 ? (
                <div className="grocery-list">
                  {groceryList.map(item => (
                    <div key={item.id} className="grocery-item">
                      <div className="grocery-checkbox">
                        <input 
                          type="checkbox" 
                          checked={item.is_purchased} 
                          readOnly
                        />
                      </div>
                      <span className="grocery-name">{item.name}</span>
                      {item.quantity && (
                        <span className="grocery-quantity">{item.quantity}</span>
                      )}
                    </div>
                  ))}
                  {stats.groceryItems > 5 && (
                    <div className="grocery-more">
                      +{stats.groceryItems - 5} more items
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>All items purchased! 🎉</p>
                </div>
              )}
            </div>
          )}

          {projects.length > 0 && (
            <div className="dashboard-card">
              <div className="card-header">
                <h2>
                  <svg className="card-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Active Projects
                </h2>
                <div className="card-header-info">
                  <span className="card-badge">{projects.length} active</span>
                </div>
              </div>
              <div className="projects-preview">
                {projects.map(project => {
                  const status = typeof project.status === 'string' ? project.status : (project.status?.value || 'planned');
                  const priority = typeof project.priority === 'string' ? project.priority : (project.priority?.value || 'medium');
                  const statusColors = {
                    'in_progress': 'var(--primary)',
                    'on_hold': 'var(--warning)',
                    'planned': 'var(--info)',
                  };
                  const priorityColors = {
                    'urgent': 'var(--error)',
                    'high': 'var(--warning)',
                    'medium': 'var(--info)',
                    'low': 'var(--success)',
                  };
                  const budgetProgress = project.budget_estimated && project.budget_estimated > 0
                    ? Math.min(100, ((project.budget_actual || 0) / project.budget_estimated) * 100)
                    : 0;

                  return (
                    <div
                      key={project.id}
                      className="project-preview-item clickable"
                      onClick={() => onNavigate && onNavigate('projects', null, project.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && onNavigate && onNavigate('projects', null, project.id)}
                      aria-label={`View project ${project.name}`}
                    >
                      <div className="project-preview-header">
                        <h3 className="project-preview-name">{project.name}</h3>
                        <div className="project-preview-badges">
                          <span 
                            className="project-status-badge" 
                            style={{ backgroundColor: statusColors[status] || statusColors.planned }}
                            title={`Status: ${status}`}
                          >
                            {status.replace('_', ' ')}
                          </span>
                          <span 
                            className="project-priority-badge" 
                            style={{ backgroundColor: priorityColors[priority] || priorityColors.medium }}
                            title={`Priority: ${priority}`}
                          >
                            {priority}
                          </span>
                        </div>
                      </div>
                      {project.description && (
                        <p className="project-preview-description">{project.description.substring(0, 100)}{project.description.length > 100 ? '...' : ''}</p>
                      )}
                      {project.budget_estimated && project.budget_estimated > 0 && (
                        <div className="project-preview-budget">
                          <div className="project-budget-info">
                            <span className="budget-label">Budget:</span>
                            <span className="budget-amount">
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(project.budget_actual || 0)} / {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(project.budget_estimated)}
                            </span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ 
                                width: `${budgetProgress}%`,
                                backgroundColor: budgetProgress > 90 ? 'var(--error)' : budgetProgress > 75 ? 'var(--warning)' : 'var(--primary)'
                              }}
                            ></div>
                          </div>
                        </div>
                      )}
                      {project.target_date && (
                        <div className="project-preview-date">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
                            <path d="M17 21v-8H7v8"></path>
                            <path d="M7 3v5h8"></path>
                          </svg>
                          Target: {new Date(project.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <MedicationAlerts houseId={selectedHouseId} />
        </div>

        <div className="dashboard-right">
          <BudgetSummary houseId={selectedHouseId} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
